import { useEffect, useState } from 'react';
import { Download, Eye, ExternalLink, FileText, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import {
  deleteDocument,
  downloadDocument,
  fetchDocumentBlobUrl,
  openDocumentInNewTab,
  type EntityType,
} from '@/shared/api/documents';

/**
 * Compact pill that displays an uploaded file with View + Delete
 * affordances. The "Ver" button fetches the file with the stored bearer
 * token and opens it as a blob URL in a new tab — anchor navigation
 * doesn't carry Authorization headers, so we have to reconstitute.
 *
 * `onDeleted` fires after a successful delete so the parent can refetch
 * or clear its local state.
 */
export function FileLink({
  entityType,
  entityId,
  fileName,
  driveItemId,
  onDeleted,
  allowDelete = true,
  compact,
  onDeleteOverride,
  canWrite,
}: {
  entityType: EntityType;
  entityId: string;
  fileName: string;
  /**
   * Specific drive item id when this pill represents one of many files
   * on the entity (multi-file mode — Recepción today). Routes the
   * preview / open / delete actions through the per-file endpoints
   * (`/file/:driveItemId`). Omit for legacy single-file entities.
   */
  driveItemId?: string;
  onDeleted?: () => void;
  allowDelete?: boolean;
  /** Smaller variant without the subtitle — used in tight table rows. */
  compact?: boolean;
  /**
   * When provided, intercepts the trash click: instead of opening the
   * confirm dialog and calling `deleteDocument`, it just invokes this
   * callback. Lets a parent stage the delete inside a larger form flow
   * (e.g. NuevaCompraPage edit mode) so the file isn't removed until
   * the user clicks Guardar cambios.
   */
  onDeleteOverride?: () => void;
  /**
   * Explicit delete authorization that OVERRIDES the global read-only role gate
   * (still AND-ed with `allowDelete`). Pass when the caller has its own per-record
   * authorization (e.g. a marketing user managing its OWN venta's comprobante).
   * Undefined → fall back to the role-based `isReadOnly` gate.
   */
  canWrite?: boolean;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [preview, setPreview] = useState<{ url: string; mimeType: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const roleReadOnly = useIsReadOnly();
  // Explicit `canWrite` wins over the role gate; still gated by `allowDelete`.
  const canDelete = canWrite === undefined ? !roleReadOnly : canWrite;
  const showDelete = allowDelete && canDelete;

  const isImage = /\.(png|jpe?g|gif|webp)$/i.test(fileName);

  const handleOpen = async () => {
    setOpening(true);
    try {
      await openDocumentInNewTab(entityType, entityId, driveItemId);
    } catch (err) {
      toast.error('No se pudo abrir el archivo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setOpening(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDocument(entityType, entityId, fileName, driveItemId);
    } catch (err) {
      toast.error('No se pudo descargar el archivo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const fetched = await fetchDocumentBlobUrl(entityType, entityId, driveItemId);
      setPreview(fetched);
    } catch (err) {
      toast.error('No se pudo cargar la vista previa', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Revoke the blob URL once the preview closes so we don't leak memory.
  useEffect(() => {
    if (!preview) return;
    return () => URL.revokeObjectURL(preview.url);
  }, [preview]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteDocument(entityType, entityId, driveItemId);
      toast.success('Archivo eliminado');
      onDeleted?.();
    } catch (err) {
      toast.error('No se pudo eliminar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          // `max-w-full` keeps the pill within its parent's width so a
          // long filename can't push siblings out (Historial de Pagos
          // dialog repro). `min-w-0` cancels flex's default
          // `min-width: auto`, which is what otherwise prevents the
          // inner truncate from engaging.
          'inline-flex max-w-full min-w-0 items-center gap-2 rounded-lg border bg-secondary/60 px-3 py-1.5 text-sm',
          compact && 'px-2 py-1 text-xs',
        )}
      >
        {isImage ? (
          <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <span
          className="min-w-0 flex-1 truncate font-medium text-foreground"
          title={fileName}
        >
          {fileName}
        </span>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewLoading}
          title="Vista previa"
          aria-label="Vista previa"
          className="ml-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          {previewLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Eye className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          title="Descargar"
          aria-label="Descargar archivo"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          {downloading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Download className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        <button
          type="button"
          onClick={handleOpen}
          disabled={opening}
          title="Abrir en pestaña nueva"
          aria-label="Abrir en pestaña nueva"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          {opening ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          )}
        </button>
        {showDelete && (
          <button
            type="button"
            onClick={() => (onDeleteOverride ? onDeleteOverride() : setConfirmOpen(true))}
            title="Eliminar"
            aria-label="Eliminar archivo"
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>

      {preview && (
        <Dialog open onOpenChange={(o) => !o && setPreview(null)}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle className="truncate">{fileName}</DialogTitle>
              <DialogDescription className="sr-only">
                Vista previa del archivo adjunto.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="p-0">
              {preview.mimeType.startsWith('image/') ? (
                <img
                  src={preview.url}
                  alt={fileName}
                  className="max-h-[70vh] w-full object-contain"
                />
              ) : preview.mimeType === 'application/pdf' ? (
                <iframe
                  src={preview.url}
                  title={fileName}
                  className="h-[70vh] w-full border-0"
                />
              ) : (
                <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
                  No se puede previsualizar este tipo de archivo. Abrilo en una
                  pestaña nueva.
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  // Open the already-fetched blob URL directly — saves a
                  // second round-trip to the API for the same bytes.
                  window.open(preview.url, '_blank');
                }}
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" aria-hidden />
                Abrir en pestaña nueva
              </Button>
              <Button type="button" onClick={() => setPreview(null)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {confirmOpen && (
        <Dialog open onOpenChange={(o) => !o && !deleting && setConfirmOpen(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Eliminar archivo</DialogTitle>
              <DialogDescription>
                Se eliminará <span className="font-medium">{fileName}</span>.
                No se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogBody />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-rose-600 text-white hover:bg-rose-700"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
